import CreateAccountContainer from 'Containers/CreateAccount.container'
import Download from 'Containers/Download'
import LoginContainer from 'Containers/Login.container'
import OnboardingContainer from 'Containers/Onboarding.container'
import PaymentContainer from 'Containers/Payment.container'
import PaymentSuccessContainer from 'Containers/PaymentSuccess.container'
import TokenLoginContainer from 'Containers/TokenLogin.container'
import UpgradeAccountContainer from 'Containers/UpgradeAccount.container'
import {
  fallbackToKnownLang,
  getAnalyticsPath,
  getAnalyticsUrl,
  getCurrentVisitUrlParams,
  sanitizeUrl
} from 'Lib'
import { getCommitEventsMap, getCurrentFlow, getGoBackToEventsMap, getGoToEventsMap } from 'Lib/EventsUtils'
import { getLastSavedStep, saveLastSavedStep } from 'Lib/SelectionsUtils'
import { RouteNames, STATIC_ROUTE_NAMES } from 'RouteNames'
import { handleFacebookStepTrack, handleTikTokStepTrack, initCookieService, trackFacebookPageView } from 'Services'
import { setGrowthbookAttributes } from 'Services/GrowthBook.service'
import * as H from 'history'
import i18n from 'i18n'
import { useEffect } from 'react'
import { Redirect, Route, Switch, useHistory, useLocation, useRouteMatch } from 'react-router-dom'
import { snapchatService, firebaseServicePromise, gTagService } from 'store'
import { ONBOARDING_STEPS } from 'types/onboardingTypes'
import { useSelector } from 'react-redux'
import { Selectors } from 'Reducers'
import SplashScreen from 'Components/SplashScreen'
import ManageSubscriptionContainer from 'Containers/ManageSubscription.container'
import PrivacyPolicyContainer from 'Components/PrivacyPolicyContainer'
import PrivacyPolicyAppContainer from 'Components/PrivacyPolicyAppContainer'
import CookiePolicyContainer from 'Components/CookiePolicyContainer'
import TermsOfUseContainer from 'Components/TermsOfUseContainer'
import WellhubWelcomeBackContainer from 'Containers/WellhubWelcomeBack.container'
import WellhubRegisterContainer from 'Containers/WellhubRegister.container'
import WellhubLoginContainer from 'Containers/WellhubLogin.container'
import UscRedeemContainer from 'Containers/UscRedeem.container'
import UscAccountContainer from 'Containers/UscAccount.container'
import SubscriptionUpdate from 'Containers/SubscriptionUpdate'
import EbookPurchaseContainer from 'Containers/EbookPurchase.container'
import EbookPurchaseSuccessContainer from 'Containers/EbookPurchaseSuccess.container'
import RefundPolicyContainer from './Components/RefundPolicyContainer'
import { SNAPCHAT_CUSTOM_EVENTS } from 'Services/Snapchat.service'
import LoadingScreen from 'Containers/Loading/LoadingScreen'

const NO_SPLASH_ROUTES: string[] = [
  RouteNames.HOME,
  RouteNames.LOADING,
  RouteNames.ONBOARDING
]

const pageViewAnalytics = () => {
  const analyticsPath = getAnalyticsPath()
  const analyticsLocation = getAnalyticsUrl()

  void firebaseServicePromise.then((firebase) => firebase.setCurrentScreen(analyticsPath))
  gTagService.setPageData(analyticsPath, analyticsLocation)
  gTagService.pageView(analyticsPath, analyticsLocation)
  snapchatService.pageView()
}

const isGoingBack = (history: H.History<H.LocationState>) => {
  const lastSavedHistoryLength = localStorage.getItem('lastSavedHistoryLength') ?? ''
  return history.action === 'POP' && lastSavedHistoryLength === history.length.toString()
}

const handleStepLog = (history: H.History<H.LocationState>) => {
  const lastSavedStep = getLastSavedStep()
  const pathNames = location.pathname.split('/')
  const currentStep = pathNames[pathNames.length - 1] as ONBOARDING_STEPS

  if ((location.pathname.includes(RouteNames.ONBOARDING) && pathNames.length === 2) || STATIC_ROUTE_NAMES.includes(location.pathname) || location.pathname.includes(RouteNames.MANAGE_SUBSCRIPTION)) {
    // it's a transition phase. We are opening /onboarding-steps before starting onboarding. We shouldn't log this phase.
    // additionally we shouldn't also log when the route name is one of the static names (privacy, cookie policy, terms of use)
    return
  }
  const flow = getCurrentFlow()
  const goingBack = isGoingBack(history)

  if (goingBack) {
    logEvent(getGoBackToEventsMap(flow, currentStep) ?? `web_go_back_to_unk_${currentStep}`)
    saveLastSavedStep(currentStep)
    return
  }

  if (lastSavedStep !== currentStep) {
    if (lastSavedStep) {
      logEvent(getCommitEventsMap(flow, lastSavedStep) ?? `web_commit_unk_${lastSavedStep}`)
      // closing the "umbrella" event of the authenticateUser step
      if (lastSavedStep === 'existing_email_login' || lastSavedStep === 'receive_email') {
        logEvent(`${getCommitEventsMap(flow, 'authenticateUser')}`)
      }

      handleFacebookStepTrack(lastSavedStep)
      // No TikTok PageView here: the SDK's HistoryObserver already fires one per
      // route change (see Services/TikTok.service.ts).
      handleTikTokStepTrack(lastSavedStep)

      if (SNAPCHAT_CUSTOM_EVENTS[lastSavedStep]) {
        snapchatService.logEvent(SNAPCHAT_CUSTOM_EVENTS[lastSavedStep]!, {})
      }
    }
    // opening the "umbrella" event of the authenticateUser step
    logEvent(getGoToEventsMap(flow, currentStep) ?? `web_go_to_unk_${currentStep}`)

    saveLastSavedStep(currentStep)
    localStorage.setItem('lastSavedHistoryLength', history.length.toString())
  }
}

const RootRouter = () => {
  const history = useHistory()
  const location = useLocation()
  const appStarted = useSelector(Selectors.isAppStarted)
  const userEmail = useSelector(Selectors.getEmail)
  const onboardingEmail = useSelector(Selectors.getOnboardingEmail)
  const noSplashRoute = useRouteMatch({
    path: NO_SPLASH_ROUTES,
    exact: true
  })

  useEffect(() => {
    initCookieService()

    const setUserLanguage = async () => {
      // Read from current-visit params (incl. INITIAL_SEARCH_PATH), NOT getSearchParams():
      // on a HOME landing the bare HOME->LOADING redirect drops the query before this
      // mount effect runs, so getSearchParams() would miss ?lang=. INITIAL_SEARCH_PATH
      // preserves the landing query, so the URL's language is honored on every entry route.
      const langCode = getCurrentVisitUrlParams().get('lang')
      const urlLangCode = langCode?.split('-')[0].toLowerCase()

      const shortLangCode = urlLangCode ?? localStorage.getItem('lang') ?? navigator.language
      const knownLanguage = fallbackToKnownLang(shortLangCode)
      if (urlLangCode) {
        setGrowthbookAttributes({
          lang: urlLangCode
        })

        localStorage.setItem('lang', knownLanguage)
      }
      await i18n.changeLanguage(shortLangCode)
    }
    void setUserLanguage()
  }, [])

  useEffect(() => {
    sanitizeUrl(history)

    if (!__DEV__) {
      pageViewAnalytics()
      trackFacebookPageView()
    }

    const goingBack = isGoingBack(history)

    // Helps us more accurately target global CSS elements after React mounts to avoid unstyled flashes
    document.body.classList.add('mounted')

    document.documentElement.style.setProperty('--enter-translate', goingBack ? '-100%' : '100%')
    document.documentElement.style.setProperty('--exit-translate', goingBack ? '100%' : '-100%')

    handleStepLog(history)
  }, [location])

  useEffect(() => {
    const email = userEmail ?? onboardingEmail

    if (email) {
      gTagService.setUserData({
        email: [email]
      })
    }
  }, [userEmail, onboardingEmail])

  if (
    !appStarted &&
    !noSplashRoute
  ) {
    return <SplashScreen />
  }

  return (
    <Switch>
      {/* INTRO should stay above the ONBOARDING */}
      <Route path={RouteNames.LOADING} component={LoadingScreen} exact />
      <Route path={RouteNames.ONBOARDING} component={OnboardingContainer} />
      <Route path={RouteNames.DOWNLOAD} component={Download} />
      <Route path={RouteNames.PAYMENT} component={PaymentContainer} />
      <Route path={RouteNames.EBOOK_PURCHASE} component={EbookPurchaseContainer} />
      <Route path={RouteNames.EBOOK_PURCHASE_SUCCESS} component={EbookPurchaseSuccessContainer} />
      <Route path={RouteNames.UPGRADE_ACCOUNT} component={UpgradeAccountContainer} />
      <Route path={RouteNames.PAYMENT_SUCCESS} component={PaymentSuccessContainer} />
      <Route path={RouteNames.UPDATE_SUBSCRIPTION} component={SubscriptionUpdate} />

      <Route path={RouteNames.LOGIN_TOKEN} component={TokenLoginContainer} />
      <Route path={RouteNames.LOGIN_PAYMENT} component={PaymentContainer} />
      <Route path={RouteNames.LOGIN_EBOOK_PURCHASE} component={EbookPurchaseContainer} />
      <Route path={RouteNames.LOGIN_EBOOK_PURCHASE_SUCCESS} component={EbookPurchaseSuccessContainer} />
      <Route path={RouteNames.LOGIN_PAYMENT_SUCCESS} component={PaymentSuccessContainer} />
      <Route path={RouteNames.LOGIN_UPDATE_SUBSCRIPTION} component={SubscriptionUpdate} />
      <Route path={RouteNames.LOGIN_UPGRADE_ACCOUNT} component={UpgradeAccountContainer} />
      <Route path={RouteNames.LOGIN_DOWNLOAD} component={Download} />
      <Route path={RouteNames.LOGIN_CREATE_ACCOUNT} component={CreateAccountContainer} />
      <Route path={RouteNames.LOGIN} component={LoginContainer} />

      <Route path={RouteNames.WELLHUB_LOGIN} component={WellhubLoginContainer} />
      <Route path={RouteNames.WELLHUB_WELCOME_BACK} component={WellhubWelcomeBackContainer} />
      <Route path={RouteNames.WELLHUB_REGISTER} component={WellhubRegisterContainer} />
      <Route path={RouteNames.WELLHUB_DOWNLOAD} component={Download} />

      <Route path={RouteNames.USC_REDEEM} component={UscRedeemContainer} />
      <Route path={RouteNames.USC_ACCOUNT} component={UscAccountContainer} />
      <Route path={RouteNames.USC_DOWNLOAD} component={Download} />

      <Route path={RouteNames.PRIVACY} component={PrivacyPolicyContainer} />
      <Route path={RouteNames.PRIVACY_APP} component={PrivacyPolicyAppContainer} />

      <Route path={RouteNames.COOKIE_POLICY} component={CookiePolicyContainer} />
      <Route path={RouteNames.TERMS_OF_USE} component={TermsOfUseContainer} />
      <Route path={RouteNames.REFUND_POLICY} component={RefundPolicyContainer} />

      <Route path={RouteNames.MANAGE_SUBSCRIPTION_PAYMENT} component={PaymentContainer} />
      <Route path={RouteNames.MANAGE_SUBSCRIPTION_PAYMENT_SUCCESS} component={PaymentSuccessContainer} />
      <Route path={RouteNames.MANAGE_SUBSCRIPTION_UPDATE_SUBSCRIPTION} component={SubscriptionUpdate} />
      <Route path={RouteNames.MANAGE_SUBSCRIPTION_DOWNLOAD} component={Download} />
      <Route path={RouteNames.MANAGE_SUBSCRIPTION} component={ManageSubscriptionContainer} />

      {/* HOME should stay on the bottom. Redirect bare to LOADING — sanitizeUrl
          (which runs on every non-HOME route) restores this visit's marketing
          params from INITIAL_SEARCH_PATH and strips secrets, so it's the single
          owner of what ends up in the URL. */}
      <Redirect from={RouteNames.HOME} to={RouteNames.LOADING} />
    </Switch>
  )
}

export default RootRouter
